/**
 * UX EXPERIMENT — /preview/swipe-picks
 * Standalone prototype (mock data) for the swipe-first pick flow.
 * Two variants behind a toggle:
 *   A) "deck"   — match-by-match horizontal scroll-snap pager (one match/screen)
 *   B) "hybrid" — swipeable group rail + scannable vertical match list
 * Reuses the REAL UnifiedGameCard so inline boosts (spread sliders) are faithful
 * and we can feel the slider-vs-swipe interaction. Picks auto-save (simulated).
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Home, Landmark, Trophy, Coins, User, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetCloseButton,
} from '@/components/ui/sheet';
import type { PickSelection } from '@prisma/client';
import type { GameData, PropData, MyPropData } from '@/services/events/main-event';
import { UnifiedGameCard } from '../../events/main/[id]/_components/game-card';
import { countryDisplayName } from '@/lib/country-flags';
import { MOCK_GROUPS, MOCK_ALL_GAMES, MOCK_PROPS, buildMyProp } from './_mock';

const POINTS_PER_PICK = 3;

type Variant = 'deck' | 'hybrid';
type PurchaseSide = 'YES' | 'NO' | 'BUY';

export default function SwipePicksPreview() {
  const [variant, setVariant] = useState<Variant>('deck');
  const [groupIdx, setGroupIdx] = useState(0);
  const [picks, setPicks] = useState<Map<string, PickSelection>>(new Map());
  const [purchases, setPurchases] = useState<Map<string, PurchaseSide>>(new Map());
  const [saved, setSaved] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Render cards client-only (mirrors the real app, which fetches after mount)
  // so locale-formatted game times don't trip SSR hydration on mock data.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Bucket mock props per game the way the real undercard page does.
  const { teamByGame, manualByGame } = useMemo(() => {
    const team = new Map<string, PropData[]>();
    const manual = new Map<string, PropData[]>();
    for (const p of MOCK_PROPS) {
      if (!p.gameId) continue;
      const isManual = p.propType && p.propType !== 'ODDS_BASED';
      const target = isManual ? manual : team;
      const arr = target.get(p.gameId) ?? [];
      arr.push(p);
      target.set(p.gameId, arr);
    }
    return { teamByGame: team, manualByGame: manual };
  }, []);

  const purchasedPropIds = useMemo(() => new Set(purchases.keys()), [purchases]);
  const myProps = useMemo(
    () => Array.from(purchases.entries())
      .map(([id, side]) => buildMyProp(id, side === 'BUY' ? undefined : side))
      .filter((p): p is MyPropData => p !== null),
    [purchases],
  );

  const flashSaved = useCallback(() => {
    setSaved(true);
    window.setTimeout(() => setSaved(false), 1400);
  }, []);

  const handlePick = useCallback((gameId: string, winner: PickSelection) => {
    setPicks((prev) => {
      const next = new Map(prev);
      if (next.get(gameId) === winner) next.delete(gameId);
      else next.set(gameId, winner);
      return next;
    });
    flashSaved();
  }, [flashSaved]);

  const handlePurchase = useCallback((propId: string, userSelection?: 'YES' | 'NO') => {
    setPurchases((prev) => new Map(prev).set(propId, userSelection ?? 'BUY'));
    flashSaved();
  }, [flashSaved]);

  const group = MOCK_GROUPS[groupIdx];
  const pickedInGroup = (g = group) => g.games.filter((gm) => picks.has(gm.id)).length;
  const totalPicked = picks.size;

  const renderCard = (gm: GameData) => (
    <UnifiedGameCard
      key={gm.id}
      game={gm}
      pick={picks.get(gm.id) ?? null}
      onPick={handlePick}
      picksDisabled={false}
      pointsPerCorrectPick={POINTS_PER_PICK}
      props={teamByGame.get(gm.id) ?? []}
      playerProps={[]}
      manualProps={manualByGame.get(gm.id) ?? []}
      myProps={myProps}
      purchasedPropIds={purchasedPropIds}
      onPurchase={handlePurchase}
      propDisabled={false}
      purchasingPropId={null}
    />
  );

  if (!mounted) return <div className="min-h-screen bg-surface" />;

  return (
    <div className="min-h-screen bg-surface text-cream pb-24">
      {/* Experiment chrome */}
      <div className="sticky top-0 z-30 bg-surface/95 backdrop-blur-md border-b border-surface-3">
        <div className="max-w-md mx-auto px-4 py-2 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold/80">
            UX v2 · Swipe Picks
          </span>
          <div className="flex rounded-lg border border-surface-3 overflow-hidden text-xs">
            {(['deck', 'hybrid'] as Variant[]).map((v) => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                className={cn(
                  'px-3 py-1 font-semibold transition-colors',
                  variant === v ? 'bg-gold text-surface' : 'text-warm-gray hover:text-cream',
                )}
              >
                {v === 'deck' ? 'A · Deck' : 'B · Hybrid'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4">
        {/* Group rail (shared) */}
        <GroupRail
          groupIdx={groupIdx}
          onSelect={setGroupIdx}
          pickedCount={(i) => pickedInGroup(MOCK_GROUPS[i])}
        />

        {variant === 'deck' ? (
          <MatchDeck
            key={group.name}
            games={group.games}
            picks={picks}
            renderCard={renderCard}
          />
        ) : (
          <HybridList games={group.games} renderCard={renderCard} />
        )}
      </div>

      {/* Floating review pill */}
      <button
        onClick={() => setSummaryOpen(true)}
        className="fixed left-1/2 -translate-x-1/2 bottom-[76px] z-30 px-4 py-2 rounded-full bg-gold text-surface text-sm font-bold shadow-lg flex items-center gap-2"
      >
        <Check className="w-4 h-4" />
        {totalPicked}/{MOCK_ALL_GAMES.length} picked · Review
      </button>

      {/* Saved toast */}
      {saved && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-[124px] z-40 px-3 py-1.5 rounded-lg bg-surface-2 border border-verdigris/30 text-verdigris text-xs font-semibold">
          Saved ✓
        </div>
      )}

      <SummarySheet
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        picks={picks}
        onJump={(gIdx) => { setGroupIdx(gIdx); setSummaryOpen(false); }}
      />

      <BottomNav />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Group rail — swipe/scroll horizontal strip with completion meter   */
/* ------------------------------------------------------------------ */
function GroupRail({
  groupIdx,
  onSelect,
  pickedCount,
}: {
  groupIdx: number;
  onSelect: (i: number) => void;
  pickedCount: (i: number) => number;
}) {
  return (
    <div className="-mx-4 px-4 py-3 sticky top-[41px] z-20 bg-surface/95 backdrop-blur-md">
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {MOCK_GROUPS.map((g, i) => {
          const picked = pickedCount(i);
          const total = g.games.length;
          const done = picked === total;
          const active = i === groupIdx;
          return (
            <button
              key={g.name}
              onClick={() => onSelect(i)}
              className={cn(
                'shrink-0 rounded-xl border px-3 py-2 text-left transition-colors',
                active
                  ? 'bg-gold/15 border-gold/40'
                  : 'bg-surface-2 border-surface-3 hover:border-surface-4',
              )}
            >
              <div className={cn('text-[11px] font-bold uppercase tracking-wider', active ? 'text-gold' : 'text-warm-gray')}>
                {g.name}
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <SegmentBar total={total} filled={picked} />
                <span className={cn('text-[10px] font-semibold', done ? 'text-verdigris' : 'text-warm-gray/70')}>
                  {done ? '✓' : `${picked}/${total}`}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SegmentBar({ total, filled, current }: { total: number; filled: number; current?: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 w-3 rounded-full',
            i === current ? 'bg-gold' : i < filled ? 'bg-verdigris' : 'bg-surface-4',
          )}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant A — match-by-match scroll-snap deck                         */
/* ------------------------------------------------------------------ */
function MatchDeck({
  games,
  picks,
  renderCard,
}: {
  games: GameData[];
  picks: Map<string, PickSelection>;
  renderCard: (g: GameData) => React.ReactNode;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  // Reset to first card when the group (games) changes.
  useEffect(() => {
    setActive(0);
    trackRef.current?.scrollTo({ left: 0 });
  }, [games]);

  // Leftmost visible card index (2-up layout).
  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const left = track.scrollLeft + 16; // account for px-4 padding
    let best = 0;
    let bestDist = Infinity;
    Array.from(track.children).forEach((child, i) => {
      const el = child as HTMLElement;
      const d = Math.abs(el.offsetLeft - left);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setActive(best);
  };

  const goTo = (i: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(games.length - 1, i));
    const el = track.children[clamped] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
  };

  const pickedFilled = games.filter((g) => picks.has(g.id)).length;
  const lastVisible = Math.min(active + 2, games.length);

  return (
    <div className="pt-2">
      {/* Position + count cue */}
      <div className="flex items-center justify-between mb-2">
        <SegmentBar total={games.length} filled={pickedFilled} current={active} />
        <span className="text-xs text-warm-gray font-medium">
          Matches {active + 1}–{lastVisible} of {games.length}
        </span>
      </div>

      {/* Scroll-snap track — 2 cards per screen; sliders inside keep pointer capture */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide -mx-4 px-4 pb-2"
        style={{ scrollPaddingLeft: '1rem' }}
      >
        {games.map((g) => (
          <div key={g.id} className="snap-start shrink-0 w-[62%]">
            {renderCard(g)}
          </div>
        ))}
      </div>

      {/* Prev/Next — page by 2; keyboard/AT/desktop fallback */}
      <div className="flex items-center justify-between mt-2">
        <button
          onClick={() => goTo(active - 2)}
          disabled={active === 0}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-2 border border-surface-3 text-sm text-warm-gray disabled:opacity-40"
        >
          <ChevronLeft className="w-4 h-4" /> Prev
        </button>
        <button
          onClick={() => goTo(active + 2)}
          disabled={lastVisible >= games.length}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-surface-2 border border-surface-3 text-sm text-warm-gray disabled:opacity-40"
        >
          Next <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Variant B — hybrid: group rail (above) + scannable match list      */
/* ------------------------------------------------------------------ */
function HybridList({
  games,
  renderCard,
}: {
  games: GameData[];
  renderCard: (g: GameData) => React.ReactNode;
}) {
  return (
    <div className="pt-3 space-y-4">
      {games.map((g) => renderCard(g))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pick summary — scannable, grouped, empties flagged                  */
/* ------------------------------------------------------------------ */
function SummarySheet({
  open,
  onClose,
  picks,
  onJump,
}: {
  open: boolean;
  onClose: () => void;
  picks: Map<string, PickSelection>;
  onJump: (groupIdx: number) => void;
}) {
  const pickLabel = (g: GameData): { text: string; empty: boolean } => {
    const p = picks.get(g.id);
    if (!p) return { text: 'Not picked', empty: true };
    return { text: countryDisplayName(p === 'HOME' ? g.homeTeam : g.awayTeam), empty: false };
  };
  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      {/* `side="bottom"` — this is a sheet by nature, not a dialog that
          becomes one on small screens, so it stays bottom-anchored at every
          width exactly as it did before. */}
      <SheetContent side="bottom" className="max-h-[80vh]" showClose={false}>
        <div className="sticky top-0 bg-surface-2 px-5 py-4 border-b border-surface-3 flex items-center justify-between">
          <div>
            <SheetTitle className="font-serif font-bold text-lg text-cream">
              Your Picks
            </SheetTitle>
            <p className="text-xs text-verdigris mt-0.5">✓ All saved automatically</p>
          </div>
          <SheetCloseButton className="-my-2 -mr-2.5" />
        </div>
        <div className="px-5 py-3 space-y-5">
          {MOCK_GROUPS.map((g, gi) => {
            const picked = g.games.filter((gm) => picks.has(gm.id)).length;
            return (
              <div key={g.name}>
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => onJump(gi)} className="text-[11px] font-bold uppercase tracking-wider text-warm-gray hover:text-gold">
                    {g.name} ›
                  </button>
                  <span className={cn('text-[10px] font-semibold', picked === g.games.length ? 'text-verdigris' : 'text-warm-gray/60')}>
                    {picked}/{g.games.length}
                  </span>
                </div>
                <div className="rounded-xl border border-surface-3 overflow-hidden">
                  {g.games.map((gm) => {
                    const lbl = pickLabel(gm);
                    return (
                      <div key={gm.id} className="flex items-center justify-between gap-3 px-3 py-2 border-t border-surface-3 first:border-t-0">
                        <span className="text-xs text-warm-gray truncate">
                          {countryDisplayName(gm.awayTeam)} v {countryDisplayName(gm.homeTeam)}
                        </span>
                        <span className={cn('text-xs font-semibold shrink-0', lbl.empty ? 'text-oxblood' : 'text-cream')}>
                          {lbl.empty ? '⚠ ' : '→ '}{lbl.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ------------------------------------------------------------------ */
/* Mock bottom navigation                                              */
/* ------------------------------------------------------------------ */
function BottomNav() {
  const items = [
    { label: 'Home', icon: Home },
    { label: 'The Mint', icon: Landmark },
    { label: 'Events', icon: Trophy, active: true },
    { label: 'Earn', icon: Coins },
    { label: 'You', icon: User },
  ];
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-30 bg-surface/95 backdrop-blur-md border-t border-surface-3"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="max-w-md mx-auto px-2 flex items-stretch justify-between">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.label}
              className={cn(
                'flex-1 flex flex-col items-center gap-0.5 py-2.5 transition-colors',
                it.active ? 'text-gold' : 'text-warm-gray/50 hover:text-warm-gray',
              )}
            >
              {it.active && <span className="absolute top-0 h-0.5 w-10 rounded-full bg-gold shadow-[0_0_12px_rgba(184,160,76,0.4)]" />}
              <Icon className={cn('w-5 h-5', it.active ? 'opacity-100' : 'opacity-60')} />
              <span className="text-[10px] font-medium">{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
